import {
  ACESFilmicToneMapping,
  PerspectiveCamera,
  Quaternion,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three'
import { Pilot } from './physics/Pilot'
import { loadConfig, saveConfig, type PresetName } from './physics/config'
import { makePilotInput, type InputSource } from './input/types'
import { GamepadSource } from './input/GamepadSource'
import { XRSource, type PoseMode } from './input/XRSource'
import { Recorder, ReplaySource, type ReplayFile } from './input/Replay'
import { PilotRig } from './render/rig'
import { DebugOverlay } from './render/debug'
import { buildWorld, type WorldKind, type WorldModel } from './render/world'
import { DomPanel } from './ui/domPanel'
import { VrPanel } from './ui/vrPanel'
import { VrHud } from './ui/vrHud'
import { Hud } from './ui/hud'
import { FirstFlight } from './game/firstFlight'
import { ThrustAudio } from './fx/audio'
import { Haptics } from './fx/haptics'
import { SIM_DT } from './sim/harness'

/** Never simulate more than this many steps in one frame — a stall must not cascade. */
const MAX_SUBSTEPS = 6
/** Seconds of tumble before respawning after a crash (DESIGN.md §6). */
const CRASH_TUMBLE_SECONDS = 1.2

type CameraMode = 'chase' | 'onboard'

export class App {
  private readonly renderer: WebGLRenderer
  private readonly scene = new Scene()
  private readonly camera: PerspectiveCamera
  private readonly pilot: Pilot
  private readonly rig: PilotRig
  private readonly debug: DebugOverlay
  private readonly hud: Hud
  private readonly panel: DomPanel
  private readonly vrPanel: VrPanel
  private readonly vrHud = new VrHud()
  private readonly world: WorldModel
  private readonly flight: FirstFlight
  private readonly haptics = new Haptics()
  private readonly audio: ThrustAudio
  private readonly recorder = new Recorder()

  private readonly gamepad: GamepadSource
  private readonly xr: XRSource
  private replay: ReplaySource | null = null

  private readonly input = makePilotInput()
  private accumulator = 0
  private lastTime = 0
  private cameraMode: CameraMode = 'chase'
  private preset: PresetName
  private inXR = false
  private muted = false
  private respawnTimer = 0
  private needsXRCalibration = false
  private hudFps = 90

  constructor(
    hudElement: HTMLElement,
    panelElement: HTMLElement,
    private readonly enterButton: HTMLButtonElement,
  ) {
    const { config, preset } = loadConfig(location.search)
    this.preset = preset
    this.pilot = new Pilot(config)

    this.renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance' })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.toneMapping = ACESFilmicToneMapping
    this.renderer.xr.enabled = true
    this.renderer.xr.setReferenceSpaceType('local-floor')
    document.body.append(this.renderer.domElement)

    this.camera = new PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 2000)
    this.camera.position.set(0, 4, 12)
    this.scene.add(this.camera)

    const worldKind = (new URLSearchParams(location.search).get('world') as WorldKind) ?? 'city'
    this.world = buildWorld(this.scene, worldKind === 'flat' ? 'flat' : 'city')
    this.pilot.world = this.world.collision
    this.rig = new PilotRig(this.scene)
    this.debug = new DebugOverlay(this.scene)
    this.hud = new Hud(hudElement)
    this.audio = new ThrustAudio(this.camera)

    this.gamepad = new GamepadSource(config.nominalHeight)
    this.xr = new XRSource(this.renderer)
    const pose = new URLSearchParams(location.search).get('pose')
    if (pose === 'aim' || pose === 'grip') this.xr.poseMode = pose as PoseMode

    this.vrPanel = new VrPanel(config, preset, (name) => {
      this.preset = name
      this.panel.syncFromConfig()
    })
    this.vrPanel.attach(this.rig.nozzles[0], this.rig.nozzles[1])
    this.vrHud.attach(this.camera)

    this.panel = new DomPanel(panelElement, config, preset, this.debug.flags, {
      onPresetChange: (name) => {
        this.preset = name
        this.vrPanel.setPreset(name)
      },
      onReset: () => this.respawn(),
      onCalibrate: () => this.calibrate(),
      onRecordToggle: () => this.toggleRecording(),
      onReplayFile: (file) => void this.loadReplay(file),
      onToggleCamera: () => this.toggleCamera(),
    })

    this.flight = new FirstFlight(this.world.launch, this.world.landing)

    this.respawn()
    this.bindEvents()
    this.setupXRButton()
    this.renderer.setAnimationLoop((time, frame) => this.frame(time, frame))
  }

  // -------------------------------------------------------------------------
  // lifecycle
  // -------------------------------------------------------------------------

  private bindEvents(): void {
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(window.innerWidth, window.innerHeight)
    })

    window.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement) return
      switch (e.code) {
        case 'KeyR':
          this.respawn()
          break
        case 'KeyC':
          this.calibrate()
          break
        case 'KeyV':
          this.toggleCamera()
          break
        case 'KeyM':
          this.muted = !this.muted
          this.audio.setMuted(this.muted)
          break
      }
    })

    // Audio needs a gesture; any first interaction will do.
    const startAudio = () => {
      this.audio.start(this.rig.nozzles)
      window.removeEventListener('pointerdown', startAudio)
      window.removeEventListener('keydown', startAudio)
    }
    window.addEventListener('pointerdown', startAudio)
    window.addEventListener('keydown', startAudio)
  }

  private setupXRButton(): void {
    const xr = navigator.xr
    if (!xr) {
      this.enterButton.textContent = 'WebXR unavailable'
      return
    }
    void xr.isSessionSupported('immersive-vr').then((supported) => {
      if (!supported) {
        this.enterButton.textContent = 'no immersive-vr device'
        return
      }
      this.enterButton.disabled = false
      this.enterButton.textContent = 'ENTER VR'
      this.enterButton.onclick = () => void this.enterXR()
    })
  }

  private async enterXR(): Promise<void> {
    const session = await navigator.xr!.requestSession('immersive-vr', {
      optionalFeatures: ['local-floor', 'bounded-floor'],
    })
    await this.renderer.xr.setSession(session)

    // The Quest browser often lands on 72 Hz. §8 makes 90 a hard requirement:
    // the app rotates the horizon, which is the worst case for nausea, and
    // frame drops are the difference between "hard" and "sickening".
    const rates = session.supportedFrameRates
    if (rates?.length && session.updateTargetFrameRate) {
      const best = [...rates].filter((r) => r <= 90).sort((a, b) => b - a)[0]
      if (best) await session.updateTargetFrameRate(best).catch(() => undefined)
    }

    // Three defaults to maximum foveation, which blurs the periphery hardest.
    // §2a says the pilot reads virtual-down almost entirely from peripheral
    // horizon cues, so trade a little fill rate to keep the edges legible.
    this.renderer.xr.setFoveation(0.4)

    this.inXR = true
    // Whatever the desktop calibrated against is meaningless now: the CoM is
    // derived from real standing height, and every lever arm in the sim is
    // measured from it. Recalibrate on the first frame with a tracked head.
    this.needsXRCalibration = true
    this.enterButton.style.display = 'none'
    this.audio.start(this.rig.nozzles)

    // Inside XR the camera must live in the rig: the headset pose is applied
    // relative to the camera's parent, and the rig is what physics moves.
    this.rig.cameraHolder.add(this.camera)
    this.rig.showBody = false
    this.vrHud.setVisible(true)

    // Record every headset session automatically. The panel's record button is
    // useless here: you cannot reach a UI you are still learning to open, and a
    // flight that went wrong is exactly the one worth keeping. Nothing is
    // uploaded — it stays in memory until you choose to save it.
    this.recorder.start()

    session.addEventListener('end', () => {
      this.inXR = false
      this.enterButton.style.display = ''
      if (this.cameraMode === 'onboard') this.rig.cameraHolder.add(this.camera)
      else this.scene.add(this.camera)
      this.rig.showBody = this.cameraMode === 'chase'
      this.vrHud.setVisible(false)
      this.xr.frame = null

      this.recorder.stop()
      if (this.recorder.frameCount > 0) {
        this.panel.offerRecording(this.recorder, this.pilot.com.standingHeight)
      }
    })
  }

  // -------------------------------------------------------------------------
  // actions
  // -------------------------------------------------------------------------

  private respawn(): void {
    _spawn
      .copy(this.world.launch.position)
      .setY(this.world.launch.position.y + this.pilot.com.height + this.pilot.cfg.bodyRadius + 0.05)
    this.pilot.reset(_spawn, _identity)
    this.flight.reset()
    this.respawnTimer = 0
  }

  private calibrate(): void {
    this.pilot.com.calibrate(this.input.head, this.pilot.cfg)
    this.gamepad.setStandingHeight(this.pilot.com.standingHeight)
    this.pilot.syncBodyProperties()
  }

  private toggleCamera(): string {
    this.cameraMode = this.cameraMode === 'chase' ? 'onboard' : 'chase'
    if (this.cameraMode === 'onboard') this.rig.cameraHolder.add(this.camera)
    else this.scene.add(this.camera)
    this.rig.showBody = this.cameraMode === 'chase'
    return this.cameraMode
  }

  private toggleRecording(): boolean {
    if (this.recorder.recording) {
      this.recorder.stop()
      this.recorder.download(this.pilot.com.standingHeight, `preset=${this.preset}`)
      return false
    }
    this.recorder.start()
    return true
  }

  private async loadReplay(file: File): Promise<void> {
    try {
      const parsed = JSON.parse(await file.text()) as ReplayFile
      if (parsed.version !== 1 || !Array.isArray(parsed.data)) throw new Error('bad replay file')
      this.replay = new ReplaySource(parsed)
      this.pilot.com.standingHeight = parsed.standingHeight
      this.respawn()
    } catch (error) {
      this.flight.lastMessage = `replay failed: ${(error as Error).message}`
      this.replay = null
    }
  }

  // -------------------------------------------------------------------------
  // frame
  // -------------------------------------------------------------------------

  /**
   * Drive a single frame by hand. Exposed for the console (`__rocketman`) so a
   * flight can be stepped through frozen, which is how you actually read a
   * debug arrow mid-tumble.
   */
  tick(timeMs: number): void {
    this.frame(timeMs, undefined)
  }

  /** Read-only peek at the simulation, for console debugging. */
  get state(): { position: Vector3; tilt: number; speed: number; throttles: number[] } {
    return {
      position: this.pilot.body.position,
      tilt: this.pilot.tiltDegrees(),
      speed: this.pilot.body.velocity.length(),
      throttles: this.pilot.debug.rockets.map((r) => r.level),
    }
  }

  private frame(time: number, frame: XRFrame | undefined): void {
    const dt = this.lastTime === 0 ? SIM_DT : Math.min(0.1, (time - this.lastTime) / 1000)
    this.lastTime = time

    const source = this.activeSource()
    if (source === this.gamepad) this.gamepad.bodyQuaternion.copy(this.pilot.body.orientation)
    if (source === this.xr) {
      this.xr.frame = frame ?? null
      this.xr.referenceSpace = this.renderer.xr.getReferenceSpace()
    }
    source.sample(this.input, dt)

    if (source === this.xr) {
      // First tracked head pose in the session defines the pilot: standing
      // height sets the CoM, and the CoM is what every lever arm is measured
      // from. Doing this before the first physics step avoids one frame of
      // flight computed against a stale body.
      if (this.needsXRCalibration && this.input.head.tracked) {
        this.needsXRCalibration = false
        this.calibrate()
        this.respawn()
        this.haptics.pulse(this.renderer.xr.getSession(), 0.5, 80)
      }
      this.handleXRButtons()

      // Thrust off while the tuning panel is up. This is what frees the
      // trigger to be a click, and it stops a slider adjustment from firing a
      // rocket while you are standing on a 90 m rooftop.
      if (this.vrPanel.visible) {
        this.input.hands[0].throttle = 0
        this.input.hands[1].throttle = 0
      }
    }
    this.recorder.capture(this.input)

    // Fixed timestep so the headset and the headless harness run identical
    // physics — a tuning number measured on the desktop has to mean something
    // in the Quest.
    this.accumulator += dt
    let steps = 0
    while (this.accumulator >= SIM_DT && steps < MAX_SUBSTEPS) {
      this.pilot.step(this.input, SIM_DT)
      this.flight.update(this.pilot, SIM_DT)
      if (this.flight.justCrashed) this.respawnTimer = CRASH_TUMBLE_SECONDS
      this.accumulator -= SIM_DT
      steps++
    }
    if (steps === MAX_SUBSTEPS) this.accumulator = 0

    // Let the tumble read for a moment before respawning (§6) — cutting
    // instantly to the pad reads as a glitch rather than as a consequence.
    if (this.respawnTimer > 0) {
      this.respawnTimer -= dt
      if (this.respawnTimer <= 0) this.respawn()
    }

    this.rig.update(this.pilot, this.input, dt)
    this.debug.update(this.pilot)
    this.audio.update(this.pilot)
    this.haptics.update(this.renderer.xr.getSession(), this.pilot, dt)
    if (this.inXR) {
      this.vrPanel.update(this.rig.nozzles, [
        this.xr.triggerHeld[0] || this.xr.interactHeld,
        this.xr.triggerHeld[1] || this.xr.interactHeld,
      ])
      if (this.vrPanel.hoverChanged) {
        this.haptics.pulse(this.renderer.xr.getSession(), 0.25, 15)
      }
      this.vrHud.update(this.pilot, this.flight, time / 1000, {
        preset: this.preset,
        fps: this.hudFps,
      })
    }

    if (this.replay?.finished) {
      this.flight.lastMessage = 'replay finished'
      this.replay = null
    }

    if (!this.inXR) this.updateChaseCamera(dt)

    this.hudFps = this.hudFps * 0.9 + (1 / Math.max(dt, 1e-4)) * 0.1
    this.hud.update(dt, this.pilot, this.flight, {
      mode: this.inXR ? 'XR' : `desktop/${this.cameraMode}`,
      input: this.replay ? 'replay' : this.inXR ? 'controllers' : this.gamepad.status,
      grip: this.replay || this.inXR ? '—' : this.gamepad.gripLabel,
      offset: this.replay || this.inXR ? undefined : this.gamepad.offset,
      worldAim: !this.replay && !this.inXR && this.gamepad.holdWorldDown,
      preset: this.preset,
      replay: this.replay ? `replay ${(this.replay.progress * 100).toFixed(0)}%` : undefined,
    })

    this.renderer.render(this.scene, this.camera)
  }

  private activeSource(): InputSource {
    if (this.replay) return this.replay
    return this.inXR ? this.xr : this.gamepad
  }

  private handleXRButtons(): void {
    const session = this.renderer.xr.getSession()
    if (this.xr.calibratePressed) {
      this.calibrate()
      this.haptics.pulse(session, 0.6, 60)
    }
    if (this.xr.respawnPressed) this.respawn()
    if (this.xr.panelTogglePressed) this.vrPanel.toggle()
    if (this.xr.poseTogglePressed) {
      this.xr.poseMode = this.xr.poseMode === 'grip' ? 'aim' : 'grip'
      this.flight.lastMessage = `nozzle pose: ${this.xr.poseMode}`
      this.haptics.pulse(session, 0.6, 60)
    }
    if (this.xr.presetCyclePressed) {
      this.preset = this.vrPanel.cyclePreset()
      this.panel.syncFromConfig()
      this.haptics.pulse(session, 0.4, 40)
    }
  }

  /**
   * World-space chase camera. Deliberately does *not* inherit the pilot's
   * attitude: watching a tumble from a camera that tumbles with it is unusable
   * for tuning, and nauseating besides. Press V for the onboard view when you
   * want to see what the headset sees.
   */
  private updateChaseCamera(dt: number): void {
    if (this.cameraMode === 'onboard') {
      this.camera.position.set(0, this.pilot.com.standingHeight, 0)
      this.camera.quaternion.identity()
      return
    }

    const target = this.pilot.body.position
    _desired.set(0, 3.2, 11).add(target)
    // Keep the camera above ground so it never clips through the grid.
    _desired.y = Math.max(_desired.y, 1.5)
    this.camera.position.lerp(_desired, 1 - Math.exp(-4 * dt))
    this.camera.lookAt(target)
  }

  /** Persist tuning on unload so a session's knob-turning is never lost. */
  save(): void {
    saveConfig(this.pilot.cfg, this.preset)
  }
}

const _spawn = new Vector3()
const _desired = new Vector3()
const _identity = new Quaternion()
